import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { Button, CircularProgress, useTheme } from "@mui/material";
import { Portal } from "../Portals";
import { isFreeEmailAddress, validateEmail, validateLastName, validateName, validateOrganisationName } from "../auth/validationHelpers";
import { ToastContainer, Zoom, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { tagsToAvoidOnBlurEvent, toastStyle } from "../../utils/toastUtils";
import { validateField } from "../../helpers/validationStratergy";
import InputField from "../input/InputField";
import posthog from "posthog-js";
import { interpretRegisterError, landingPathAfterLogin, maskEmail, orgErrorMessage, registerAccount, storeSession, verifyRegistration } from "../../utils/organisationApi";
import useFocusTrap from "../../hooks/useFocusTrap";
import OtpStep from "./OtpStep";

function RegistrationPage() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    organisation_name: "",
    email: "",
  });

  const [errors, setErrors] = useState({
    first_name: "",
    last_name: "",
    organisation_name: "",
    email: "",
    apiError: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  // Set when the email's company domain already has an organisation
  const [existingOrganisation, setExistingOrganisation] = useState(null);
  const [joinMessage, setJoinMessage] = useState("");
  const [joinNotice, setJoinNotice] = useState("");
  const [joinRequestSent, setJoinRequestSent] = useState(false);
  // Set once POST /register/ has emailed the code for POST /register/verify
  const [codeSent, setCodeSent] = useState(false);
  const isLoadingRef = useRef(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const organisationLabel = existingOrganisation?.name || existingOrganisation?.domain || "This company";
  const maskedOwnerEmail = maskEmail(existingOrganisation?.ownerEmail);
  const defaultOrganisationName = formData.first_name.trim()
    ? `${formData.first_name.trim()}'s organisation`
    : "your first name's organisation";
  const showFreeEmailHint = isFreeEmailAddress(formData.email);
  isLoadingRef.current = isLoading;

  const closeInviteModal = () => {
    if (isLoadingRef.current) return;
    setExistingOrganisation(null);
  };
  const inviteModalRef = useFocusTrap(Boolean(existingOrganisation), closeInviteModal);

  useEffect(() => {
    const token = localStorage.getItem("jwtToken");
    if (token) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({ ...formData, [name]: value });
    setErrors((prevErrors) => ({ ...prevErrors, [name]: validateField(name, value), apiError: "" }));
    if (name === "email") {
      setExistingOrganisation(null);
      setJoinMessage("");
    }
  };

  const handleBlur = (e) => {
    setTimeout(() => {
      const nextElement = document.activeElement;
      if (!tagsToAvoidOnBlurEvent.includes(nextElement.tagName)) {
        handleChange(e);
      }
    }, 0);
  }

  const fieldErrors = () => {
    const { first_name, last_name, organisation_name, email } = formData;
    return {
      first_name: validateName(first_name),
      last_name: validateLastName(last_name),
      organisation_name: validateOrganisationName(organisation_name),
      email: validateEmail(email),
    };
  };

  const finishRegistration = (result) => {
    if (result.httpStatus === 202 || result.status === "join_requested") {
      if (result.organisationName) {
        setExistingOrganisation((prev) => ({
          ...prev,
          name: result.organisationName,
          domain: prev?.domain || formData.email.split("@")[1] || "",
        }));
      }
      setJoinNotice(result.msg || "");
      setJoinRequestSent(true);
      return;
    }
    setCodeSent(true);
  };

  const registrationFields = () => ({
    email: formData.email,
    first_name: formData.first_name,
    last_name: formData.last_name,
    organisation_name: formData.organisation_name,
  });

  // Asks for a new code by sending the same sign-up again. Returns an error message, or nothing.
  const handleResendCode = async () => {
    try {
      await registerAccount(registrationFields());
      return "";
    } catch (err) {
      return interpretRegisterError(err).message || "We couldn't send a new code. Try again.";
    }
  };

  const handleVerifyCode = async (otp) => {
    let data;
    try {
      data = await verifyRegistration({ email: formData.email, otp });
    } catch (err) {
      return orgErrorMessage(err, "That code is wrong or has expired.");
    }
    const user = storeSession(data);
    if (!user) {
      // Account created without a session: log in with the same email
      navigate(`/login?email=${encodeURIComponent(formData.email.trim())}`, { replace: true });
      return "";
    }
    posthog.identify(user.email, {
      user_id: user.user_id,
      first_name: user.first_name,
      last_name: user.last_name,
    });
    navigate(landingPathAfterLogin(), { replace: true });
    return "";
  };

  const applyRegisterFailure = (err, newErrors) => {
    const failure = interpretRegisterError(err);
    if (failure.kind === "field") {
      setErrors({ ...newErrors, [failure.field]: failure.message, apiError: "" });
      return;
    }
    if (failure.kind === "login") {
      navigate(`/login?email=${encodeURIComponent(formData.email.trim())}`, {
        replace: true,
        state: { notice: failure.message },
      });
      return;
    }
    if (failure.kind === "org_exists") {
      setExistingOrganisation({
        name: failure.organisationName,
        domain: failure.domain || formData.email.split("@")[1] || "",
        ownerEmail: failure.ownerEmail,
      });
      setErrors({ ...newErrors, apiError: "" });
      return;
    }
    const message = failure.message || "Registration failed. Try again.";
    toast.error(message, { style: toastStyle });
    setErrors({ ...newErrors, apiError: message });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = fieldErrors();
    setErrors({ ...newErrors, apiError: "", message: "" });
    if (Object.values(newErrors).some((error) => error)) return;

    try {
      setIsLoading(true);
      const result = await registerAccount(registrationFields());
      finishRegistration(result);
    } catch (err) {
      applyRegisterFailure(err, newErrors);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestInvitation = async () => {
    const newErrors = fieldErrors();
    const messageError = joinMessage.length > 1000 ? "Note must be 1000 characters or fewer." : "";
    setErrors({ ...newErrors, message: messageError, apiError: "" });
    if (Object.values(newErrors).some(Boolean) || messageError) return;

    try {
      setIsLoading(true);
      const result = await registerAccount({
        ...registrationFields(),
        request_invitation: true,
        message: joinMessage,
      });
      finishRegistration(result);
    } catch (err) {
      applyRegisterFailure(err, newErrors);
    } finally {
      setIsLoading(false);
    }
  };

  const shell = (children, { showSignIn = true } = {}) => (
    <div className="min-h-screen bg-[#f4f7fb] flex flex-col items-center justify-center px-4 py-8 sm:px-6">
      <img src="/sift-hire-logo.png" alt="SiftHire" className="w-40 mb-5" />
      <div className="w-full max-w-2xl min-w-0">
        <div className="bg-white rounded-xl border border-[#d5deec] shadow-[0_10px_30px_rgba(34,82,165,0.08)] px-5 py-7 sm:px-8 sm:py-8">
          {children}
        </div>
        {showSignIn && (
          <p className="text-accent-gray-medium text-sm text-center mt-4">
            Already using Sift Hire?{" "}
            <Link to="/login" className="text-accent-blue font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        )}
      </div>
      <ToastContainer
        position="bottom-center"
        autoClose={3000}
        hideProgressBar
        transition={Zoom}
        closeOnClick
        draggable
        closeButton={false}
        limit={1}
      />
    </div>
  );

  if (joinRequestSent) {
    return shell(
      <div className="text-center" role="status">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#e7eef8] text-accent-blue">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M20 6.5L9.5 17L4 11.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h1 className="text-2xl font-semibold text-black">Request sent</h1>
        <p className="text-accent-gray-medium mt-3">
          {joinNotice || `Your request to join ${organisationLabel} has been sent to its admins.`}
        </p>
        <p className="text-sm text-accent-gray-medium mt-3">
          Once they approve you, we'll send an invite link to <span className="font-semibold text-black">{formData.email}</span>.
        </p>
        <Link
          to="/login"
          className="mt-6 inline-flex justify-center w-full py-2.5 px-4 rounded-md text-accent-blue font-semibold border border-[#c5d4ef] hover:bg-[#f4f7fc]"
        >
          Back to sign in
        </Link>
      </div>,
      { showSignIn: false }
    );
  }

  if (codeSent) {
    return shell(
      <div className="max-w-md mx-auto">
        <OtpStep
          email={formData.email.trim()}
          onVerify={handleVerifyCode}
          onResend={handleResendCode}
          onBack={() => setCodeSent(false)}
          verifyLabel="Verify and start free trial"
          verifyingLabel="Starting your free trial"
        />
      </div>
    );
  }

  return shell(
    <>
    <form onSubmit={handleSubmit} className="flex flex-col gap-5 min-w-0">
      <div>
        <h1 className="text-2xl font-semibold text-black text-center mb-4">Create your workspace</h1>
        {/* <p className="text-sm text-accent-gray-medium mt-1">
          Use your company email. The first person from a company becomes its Owner.
        </p> */}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="min-w-0">
        <InputField
          id="first_name"
          name="first_name"
          placeholder="Enter your first name"
          type="text"
          value={formData.first_name}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          autoFocus
          className="min-w-0"
          errorState={errors.first_name}
          label="First name"
        />
        </div>
        <div className="min-w-0">
        <InputField
          id="last_name"
          name="last_name"
          placeholder="Enter your last name"
          type="text"
          value={formData.last_name}
          onChange={handleChange}
          onBlur={handleBlur}
          required
          className="min-w-0"
          errorState={errors.last_name}
          label="Last name"
        />
        </div>
      </div>

      <div>
        <InputField
          id="email"
          name="email"
          placeholder="Enter your work email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          onBlur={handleChange}
          required
          className="min-w-0"
          errorState={errors.email}
          label="Work email"
        />
        {!errors.email && showFreeEmailHint && (
          <p className="text-accent-red text-sm mt-1">
            Gmail, Yahoo, and Outlook need an invitation from your company.
          </p>
        )}
      </div>

      <div className="border-t border-[#e6ebf2] pt-5 flex flex-col gap-5">
        <div>
          <InputField
            id="organisation_name"
            name="organisation_name"
            placeholder="Enter your organisation name"
            type="text"
            value={formData.organisation_name}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={120}
            className="min-w-0"
            errorState={errors.organisation_name}
            label="Organisation name"
          />
        </div>
      </div>

      {errors.apiError && (
        <p className="text-sm text-accent-red" role="alert">{errors.apiError}</p>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className={`w-full flex justify-center items-center gap-2 py-2.5 px-4 border border-transparent text-base font-semibold rounded-md text-white transition duration-150 ease-in-out ${isLoading ? "bg-accent-blue cursor-not-allowed" : "bg-accent-blue hover:bg-accent-blue-hover"}`}
      >
        {isLoading && !existingOrganisation ? (
          <>
            Sending code
            <div className="animate-spin rounded-full border-t-2 border-white h-4 w-4"></div>
          </>
        ) : (
          "Start free trial"
        )}
      </button>
    </form>
    {existingOrganisation && (
      <Portal>
        <div
          className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-70"
          onClick={closeInviteModal}
        >
          <div
            ref={inviteModalRef}
            className="bg-white rounded-lg w-full max-w-lg max-h-[calc(100dvh-140px)] overflow-hidden relative flex flex-col shadow-xl max-sm:mx-2"
            role="dialog"
            aria-modal="true"
            aria-labelledby="join-request-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className="flex shrink-0 justify-between items-center gap-3 py-4 px-5 border-b"
              style={{ borderColor: theme.palette.secondary.v4 }}
            >
              <h2 id="join-request-title" className="font-semibold">
                Your organisation is already on SiftHire
              </h2>
              <button
                type="button"
                onClick={closeInviteModal}
                disabled={isLoading}
                aria-label="Close modal"
                className="inline-flex shrink-0 items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors disabled:cursor-not-allowed"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="flex flex-col px-5 sm:px-6 py-6 gap-4 min-h-0 overflow-y-auto">
              <p className="text-sm text-accent-gray-medium">
                Your team already has a organisation,{" "}
                <span className="font-semibold text-black">{organisationLabel}</span>. Ask your owner
                {maskedOwnerEmail ? (
                  <>
                    {" "}(<span className="font-semibold text-black break-all">{maskedOwnerEmail}</span>)
                  </>
                ) : null}{" "}
                for an invitation, or send them a request below. 
              </p>
              <div>
                <label
                  htmlFor="join_message"
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.palette.text.main }}
                >
                  Note to admins <span className="text-accent-gray-medium">(optional)</span>
                </label>
                <textarea
                  id="join_message"
                  name="message"
                  maxLength={1000}
                  rows={3}
                  value={joinMessage}
                  onChange={(e) => {
                    setJoinMessage(e.target.value);
                    setErrors((prev) => ({ ...prev, message: "", apiError: "" }));
                  }}
                  placeholder="Tell the admins who you are"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                  style={{ borderColor: theme.palette.secondary.v4, color: "#111111" }}
                />
                <div className="mt-1 flex items-center justify-between gap-3">
                  {errors.message ? <p className="text-accent-red text-sm">{errors.message}</p> : <span />}
                  <p className="text-xs text-accent-gray-light ml-auto">{joinMessage.length}/1000</p>
                </div>
              </div>
              {errors.apiError && (
                <p className="text-sm text-accent-red" role="alert">{errors.apiError}</p>
              )}
            </div>

            {/* Modal Footer */}
            <div
              className="flex shrink-0 justify-end items-center gap-3 px-5 py-4 border-t"
              style={{ borderColor: theme.palette.secondary.v4 }}
            >
              <Button
                onClick={handleRequestInvitation}
                disabled={isLoading}
                sx={{
                  backgroundColor: theme.palette.info.main,
                  color: theme.palette.text.light,
                  textTransform: "none",
                }}
              >
                {isLoading ? (
                  <span className="inline-flex items-center gap-2">
                    <CircularProgress size={14} sx={{ color: "inherit" }} />
                    Sending request...
                  </span>
                ) : (
                  "Request an invitation"
                )}
              </Button>
            </div>
          </div>
        </div>
      </Portal>
    )}
    </>
  );
}

export default RegistrationPage;
